/**
 * @file Representation of various date types in ACE.
 * @author andreas.z.hellstrom@teliacompany.com (Andreas Hellström)
 */

//------------------------------------------------------------------------------
// Requirements & Definitions
//------------------------------------------------------------------------------

import dateFormat from 'dateformat';

import { isValidDate } from './dateUtils';

// date.format.js format string definition for an ACE DB date.
export const ACE_DB_DATE_FORMAT_STRING = 'yyyymmdd HH:MM:ss';

//------------------------------------------------------------------------------
// Typedefs
//------------------------------------------------------------------------------

/**
 * Representation of a date in ACE Database date format.
 * @typedef {string} AceDBDate
 * @desc String in date.format.js format 'yyyymmdd HH:MM:ss'.
 * @desc Source: http://stevenlevithan.com/assets/misc/date.format.js
 * @example '20190131 17:01:59'
 */

/**
 * Representation of a date in ACE Web API date format.
 * @typedef {string} AceWebApiDate
 * @desc String in RFC-3339 DATE format. Compatible with Date format ISO-8601. Compatible with
 * JavaScript Date type so that this JavaScript command result in a proper JavaScript Date:
 * // new Date(RFC-3339-FORMATTED-DATE-STRING)
 * Source: https://www.ietf.org/rfc/rfc3339.txt
 * @example '2019-01-14T18:25:43Z' (UTC time)
 */

/**
 * Representation of a date in Web SDK date format.
 * @typedef {Date} WebSdkDate
 * @desc JavaScript Date type. Compatible with Date formats ISO-8601 and RFC-3339, so that these
 * JavaScript commands result in a proper JavaScript Date:
 * // new Date(ISO-8601-FORMATTED-DATE-STRING)
 * // new Date(RFC-3339-FORMATTED-DATE-STRING)
 * Source: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date
 * @example Date('2019-04-23T09:30:00.800+01:00') (UTC+1 time)
 */

//------------------------------------------------------------------------------
// Exported functions
//------------------------------------------------------------------------------

/**
 * Converts an ACE DB date to a Web SDK date.
 * @param {AceDBDate} aceDBDate - Date in ACE Database date format
 * @returns {WebSdkDate} Date in Web SDK date format
 * @throws {TypeError} for invalid input.
 * @example
 * // aceDBDateToWebSdkDate('20190130 17:01:59')
 * // Result: Date('2019-01-30 17:01:59')
 */
export function aceDBDateToWebSdkDate(aceDBDate) {
  if (!isValidAceDBDate(aceDBDate)) {
    throw new TypeError('Argument to aceDBDateToWebSdkDate() is not of AceDBDate type!');
  }

  const year = aceDBDate.substr(0, 4);
  const month = aceDBDate.substr(4, 2);
  const day = aceDBDate.substr(6, 2);
  const time = aceDBDate.substr(9, 8);

  return new Date(`${year}-${month}-${day} ${time}`);
}

/**
 * Converts an ACE Web API date to a Web SDK date.
 * @param {AceWebApiDate} aceWebApiDate - Date in ACE Web API date format
 * @returns {WebSdkDate} Date in Web SDK date format
 * @throws {TypeError} for invalid input.
 * @example With computer timezone of UTC+1 (Stockholm):
 * // aceWebApiDateToWebSdkDate('2019-01-30T16:01:59.000Z')))
 * // Result: Date('2019-01-30 17:01:59+01:00')
 */
export function aceWebApiDateToWebSdkDate(aceWebApiDate) {
  if (!isValidAceWebApiDate(aceWebApiDate)) {
    throw new TypeError('Argument to aceWebApiDateToWebSdkDate() is not of AceWebApiDate type!');
  }
  return new Date(Date.parse(aceWebApiDate));
}

/**
 * Converts a Web SDK date to an ACE DB date.
 * @param {WebSdkDate} webSdkDate - Date in Web SDK date format
 * @returns {AceDBDate} Date in ACE DB date format
 * @throws {TypeError} for invalid input.
 * @example
 * // webSdkDateToAceDBDate(new Date('2019-01-30 17:01:59'))
 * // Result: '20190130 17:01:59'
 */
export function webSdkDateToAceDBDate(webSdkDate) {
  if (!isValidWebSdkDate(webSdkDate)) {
    throw new TypeError('Argument to webSdkDateToAceDBDate() is not of WebSdkDate type!');
  }

  return dateFormat(webSdkDate, ACE_DB_DATE_FORMAT_STRING);
}

/**
 * Converts a Web SDK date to an ACE Web API date.
 * @param {WebSdkDate} webSdkDate - Date in Web SDK date format
 * @returns {AceWebApiDate} Date in ACE Web API date format
 * @example With computer timezone of UTC+1 (Stockholm):
 * // webSdkDateToAceWebApiDate(new Date('2019-01-30 17:01:59')))
 * // Result: '2019-01-30T16:01:59.000Z'
 */
export function webSdkDateToAceWebApiDate(webSdkDate) {
  return webSdkDate.toISOString();
}

/**
 * Checks if object (likely) is a valid Ace DB date. The check is just a simple sanity check.
 * @param {AceDBDate} obj
 * @returns {boolean}
 */
export function isValidAceDBDate(obj) {
  if (typeof obj !== 'string') {
    return false;
  }
  if (obj.length !== ACE_DB_DATE_FORMAT_STRING.length) {
    return false;
  }
  return true;
}

/**
 * Checks if object (likely) is a valid Ace Web API date. The check is just a simple sanity check.
 * @param {AceWebApiDate} obj
 * @returns {boolean}
 */
export function isValidAceWebApiDate(obj) {
  return typeof obj === 'string';
}

/**
 * Checks if object (likely) is a valid Web SDK date. The check is just a simple sanity check.
 * Source: https://stackoverflow.com/questions/643782/how-to-check-whether-an-object-is-a-date
 * @param {WebSdkDate} obj
 * @returns {boolean}
 */
export function isValidWebSdkDate(obj) {
  return isValidDate(obj);
}
